import { Children, isValidElement, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DotsSixVertical, Eye, EyeSlash, PushPin, PushPinSlash, SlidersHorizontal, ArrowCounterClockwise, Check } from "@phosphor-icons/react";

// Marker: <W id="stats" title="Key figures">…</W>
export const W = ({ children }) => children;

const EMPTY = { order: [], hidden: [], pinned: [] };

export function DashboardWidgets({ children }) {
  const { user } = useAuth();
  const saved = user?.preferences?.dashboard_layout;
  const [layout, setLayout] = useState(() => ({ ...EMPTY, ...(saved || {}) }));
  const [editing, setEditing] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  useEffect(() => { if (saved) setLayout({ ...EMPTY, ...saved }); }, [saved]);

  const all = Children.toArray(children).filter((c) => isValidElement(c) && c.props.id);
  const byId = Object.fromEntries(all.map((c) => [c.props.id, c]));
  const known = all.map((c) => c.props.id);
  const ordered = [...layout.order.filter((id) => byId[id]), ...known.filter((id) => !layout.order.includes(id))];
  const pinned = ordered.filter((id) => layout.pinned.includes(id));
  const rest = ordered.filter((id) => !layout.pinned.includes(id));
  const sequence = [...pinned, ...rest];
  const hiddenIds = sequence.filter((id) => layout.hidden.includes(id));

  const persist = async (next) => {
    setLayout(next);
    try { await api.put("/auth/me/dashboard-layout", next); } catch { toast.error("Could not save dashboard layout"); }
  };
  const toggle = (key, id) => persist({ ...layout, order: sequence, [key]: layout[key].includes(id) ? layout[key].filter((x) => x !== id) : [...layout[key], id] });
  const reset = () => persist({ ...EMPTY });
  const drop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const seq = sequence.filter((id) => id !== dragId);
    seq.splice(seq.indexOf(targetId), 0, dragId);
    const pin = layout.pinned.includes(targetId) ? [...new Set([...layout.pinned, dragId])] : layout.pinned.filter((id) => id !== dragId);
    persist({ ...layout, order: seq, pinned: pin });
    setDragId(null); setOverId(null);
  };

  return (
    <div className="space-y-8 hd-widgets" data-testid="dashboard-widgets">
      <div className="flex flex-wrap items-center justify-end gap-2 -mt-4" data-testid="dashboard-widgets-toolbar">
        {editing && layout.order.length + layout.hidden.length + layout.pinned.length > 0 && (
          <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={reset} data-testid="widgets-reset"><ArrowCounterClockwise size={14} className="mr-1.5" /> Reset layout</Button>
        )}
        <Button variant={editing ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setEditing((e) => !e)} data-testid="widgets-customize">
          {editing ? <><Check size={14} className="mr-1.5" /> Done</> : <><SlidersHorizontal size={14} className="mr-1.5" /> Customise</>}
        </Button>
      </div>
      {editing && hiddenIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-border/70 bg-muted/30 px-4 py-3 text-sm" data-testid="widgets-hidden-tray">
          <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mr-1">Hidden</span>
          {hiddenIds.map((id) => (
            <button key={id} onClick={() => toggle("hidden", id)} data-testid={`widget-show-${id}`} className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-3 py-1 text-xs font-medium hover:border-primary/40 hover:text-primary">
              <Eye size={13} /> {byId[id].props.title}
            </button>
          ))}
        </div>
      )}
      {sequence.map((id) => {
        const el = byId[id];
        const hidden = layout.hidden.includes(id);
        const isPinned = layout.pinned.includes(id);
        if (hidden && !editing) return null;
        return (
          <section
            key={id}
            data-testid={`widget-${id}`}
            data-widget={id}
            data-hidden={hidden ? "1" : "0"}
            data-pinned={isPinned ? "1" : "0"}
            draggable={editing}
            onDragStart={() => setDragId(id)}
            onDragOver={(e) => { if (editing) { e.preventDefault(); setOverId(id); } }}
            onDragLeave={() => setOverId((o) => (o === id ? null : o))}
            onDrop={() => drop(id)}
            className={`relative min-w-0 max-w-full overflow-x-hidden transition-all ${editing ? "rounded-2xl ring-1 ring-dashed ring-primary/30 p-2 -m-2 cursor-grab active:cursor-grabbing" : ""} ${overId === id && dragId !== id ? "ring-2 ring-primary bg-primary/5" : ""} ${hidden ? "opacity-45" : ""}`}
          >
            {editing && (
              <div className="flex items-center gap-1 mb-2 pl-1" data-testid={`widget-controls-${id}`}>
                <DotsSixVertical size={18} className="text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex-1 truncate">{el.props.title}</span>
                {isPinned && <span className="text-[10px] uppercase tracking-wider font-bold text-primary mr-1">Pinned</span>}
                <button onClick={() => toggle("pinned", id)} title={isPinned ? "Unpin" : "Pin to top"} data-testid={`widget-pin-${id}`} className={`h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-muted ${isPinned ? "text-primary" : "text-muted-foreground"}`}>
                  {isPinned ? <PushPinSlash size={16} /> : <PushPin size={16} />}
                </button>
                <button onClick={() => toggle("hidden", id)} title={hidden ? "Show" : "Hide"} data-testid={`widget-hide-${id}`} className="h-8 w-8 inline-flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted">
                  {hidden ? <Eye size={16} /> : <EyeSlash size={16} />}
                </button>
              </div>
            )}
            {!isPinned || editing ? null : <span className="absolute -top-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow" data-testid={`widget-pinned-badge-${id}`}><PushPin size={10} weight="fill" /> Pinned</span>}
            <div className={`${editing ? "pointer-events-none select-none" : ""} ${isPinned && !editing ? "pt-1" : ""}`}>{el.props.children}</div>
          </section>
        );
      })}
    </div>
  );
}
